import { FC, FocusEvent, PointerEvent, RefObject, useEffect, useRef, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CaseloadReading, clientDisplayName, READING_LABEL } from '@innerhive-internal/innerhive-common';
import { IAvatarIdentity } from '../../avatar/IAvatarIdentity';
import { PersonAvatar } from '../../avatar/Avatars';
import { BRAND, hexCentre, READING_COLOR } from './agencyChartTheme';
import { InsightsNarration } from './InsightsNarration';

export interface IHoneycombCell {
    careMapId: string;
    clientName: string;
    /** The reading the cell is rimmed in, the same one every other surface draws this client with. */
    reading: CaseloadReading;
    /** How the client is drawn, with the audited link to a hosted photo. */
    identity: IAvatarIdentity | undefined;
}

/** The cell radius on a screen with room for it; a narrower one shrinks the cells to fit its width. */
const RADIUS = 36;
const MIN_RADIUS = 16;
const COLUMNS = 6;
/** The reading-coloured rim round each photo, thick enough to read at a glance. */
const RIM = 4;
/** The space between neighbouring cells: as wide as a rim, so the rims never touch and the white reads as even. */
const GAP = RIM;
/** A pointy-top hexagon, the care map's own shape. */
const HEX_CLIP = 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)';

const cellWidthOf = (radius: number) => Math.sqrt(3) * radius;

/**
 * The grid for a caseload of `count` cells in the width there is, so a phone shows the whole
 * caseload rather than its left edge. No width yet, or none at all (the print sheet is hidden
 * until the printer lays it out), draws it at full size rather than at its smallest.
 */
const layoutOf = ({ count, wrapWidth, maxRadius }: { count: number; wrapWidth: number | undefined; maxRadius: number }) => {
    const columns = Math.min(COLUMNS, count);
    const rows = Math.ceil(count / columns);
    // The half-cell stagger on odd rows makes the grid half a cell wider than the column count.
    const columnsWide = columns + (rows > 1 ? 0.5 : 0);
    const radius = !wrapWidth
        ? maxRadius
        : Math.max(MIN_RADIUS, Math.min(maxRadius, Math.floor(wrapWidth / (Math.sqrt(3) * columnsWide))));
    return { columns, radius, width: cellWidthOf(radius) * columnsWide, height: radius * 1.5 * (rows - 1) + radius * 2 };
};

/** Whether an element's focus came from the keyboard. */
const isKeyboardFocus = (element: Element): boolean => {
    try {
        return element.matches(':focus-visible');
    } catch {
        // A browser without :focus-visible (WebKit before 15.4) throws on the selector: there the
        // tip never shows on focus, which costs only the keyboard hint.
        return false;
    }
};

/** Where the tip's top left corner goes, in viewport coordinates, and the client it names. */
interface ITipAnchor {
    name: string;
    x: number;
    y: number;
}

/** The name tip as drawn: its top left corner, relative to the map. */
interface ICursorTip {
    name: string;
    x: number;
    y: number;
}

/** A rest before the name shows, as a tooltip does, so sweeping across the map names nobody. */
const TIP_DELAY_MS = 350;
/** Clear of the cursor, so the pointer never covers the name. */
const TIP_OFFSET = { x: 14, y: 18 };
/** Under a cell focused from the keyboard. */
const FOCUS_GAP = 4;

/**
 * The name tip's state: it appears a short rest after a cell is reached, follows the pointer while
 * it stays, and goes the moment it leaves. One tip for the whole map, since only one cell can be
 * under the cursor. Kept relative to the map, which is where it is drawn.
 */
const useCursorTip = (map: RefObject<HTMLDivElement>) => {
    const [tip, setTip] = useState<ICursorTip | undefined>();
    const pending = useRef<ICursorTip | undefined>();
    const timer = useRef<number | undefined>();

    const toTip = (at: ITipAnchor): ICursorTip => {
        const origin = map.current?.getBoundingClientRect();
        return origin ? { ...at, x: at.x - origin.left, y: at.y - origin.top } : at;
    };

    const leave = () => {
        window.clearTimeout(timer.current);
        timer.current = undefined;
        pending.current = undefined;
        setTip(undefined);
    };
    useEffect(() => leave, []);

    const rest = () => {
        window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => {
            setTip(pending.current);
            pending.current = undefined;
        }, TIP_DELAY_MS);
    };

    /** A cell has been reached: its name shows after the rest. */
    const enter = (at: ITipAnchor) => {
        leave();
        pending.current = toTip(at);
        rest();
    };

    /** The pointer is moving over a cell. Over another cell than the tip names, it starts again. */
    const move = (at: ITipAnchor) => {
        const named = pending.current ?? tip;
        if (named && named.name !== at.name) {
            enter(at);
            return;
        }
        const next = toTip(at);
        if (pending.current) {
            pending.current = next;
            rest();
        }
        setTip(current => (current ? next : current));
    };

    return { tip, enter, move, leave };
};

/**
 * The width of an element, kept current as it is laid out and resized. Hands back a callback ref
 * rather than reading a ref object, so an element mounted after the first render (the map renders
 * nothing until it has cells) is still observed.
 */
const useWidth = (): { ref: (element: HTMLDivElement | null) => void; width: number | undefined } => {
    const [element, setElement] = useState<HTMLDivElement | null>(null);
    const [width, setWidth] = useState<number | undefined>();
    useEffect(() => {
        if (!element) {
            return undefined;
        }
        const observer = new ResizeObserver(entries => setWidth(entries[0].contentRect.width));
        observer.observe(element);
        return () => observer.disconnect();
    }, [element]);
    return { ref: setElement, width };
};

const useStyles = createUseStyles({
    // The origin the name tip is placed from.
    map: {
        position: 'relative'
    },

    // A caseload wider than the viewport scrolls itself rather than pushing the page sideways.
    wrap: {
        overflowX: 'auto',
        paddingBottom: 4
    },

    // Centred in its report, so a small caseload sits in the middle of the card rather than against
    // its left edge with the rest of the width empty. Auto margins and not flex centring: a centred
    // flex child wider than its box overflows both edges, and the left one cannot be scrolled to.
    field: {
        position: 'relative',
        margin: '0 auto'
    },

    cell: {
        position: 'absolute',
        padding: 0,
        border: 0,
        clipPath: HEX_CLIP,
        cursor: 'pointer',
        transition: 'opacity 120ms ease',

        // No ring, no outline. A caseload map is a field of shapes and a box drawn round one of them
        // reads as a selection that persists, which it does not: the click opens a dialog and leaves.
        '&:focus': {
            outline: 'none'
        },
        '&:hover $face, &:focus-visible $face': {
            opacity: 0.82
        }
    },

    // The photo, inside the rim, clipped to the same hexagon.
    face: {
        position: 'absolute',
        inset: RIM,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        clipPath: HEX_CLIP,
        background: 'var(--ion-background-color)',
        transition: 'opacity 120ms ease'
    },

    '@keyframes tipIn': {
        from: { opacity: 0, transform: 'translateY(4px)' },
        to: { opacity: 1, transform: 'none' }
    },

    // The name tip, a child of the map rather than of the scroller, which would cut it off at the
    // map's edge.
    tip: {
        position: 'absolute',
        zIndex: 1,
        padding: '4px 8px',
        borderRadius: 6,
        background: BRAND.deeperTeal,
        color: '#fff',
        fontSize: 12,
        fontWeight: 500,
        lineHeight: 1.3,
        whiteSpace: 'nowrap' as const,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.18)',
        pointerEvents: 'none' as const,
        animation: '$tipIn 140ms ease-out'
    },

    // The name for assistive tech and session replay, where an attribute could not carry it masked.
    hidden: {
        position: 'absolute',
        width: 1,
        height: 1,
        overflow: 'hidden',
        clip: 'rect(0 0 0 0)',
        whiteSpace: 'nowrap' as const
    },

});

/**
 * One client's cell: their photo inside a rim of their standing's colour, their name for assistive
 * tech, and their name as a tip beside a pointer that rests on them or under the cell when it is
 * focused from the keyboard.
 */
const HoneycombCell: FC<{
    cell: IHoneycombCell;
    /** The centre of the cell in the field. */
    cx: number;
    cy: number;
    /** The drawn hexagon's radius, which is the grid's less what leaves the gap between neighbours. */
    radius: number;
    isDimmed: boolean;
    onSelect: () => void;
    onTipEnter: (at: ITipAnchor) => void;
    onTipMove: (at: ITipAnchor) => void;
    onTipLeave: () => void;
}> = ({ cell, cx, cy, radius, isDimmed, onSelect, onTipEnter, onTipMove, onTipLeave }) => {
    const classes = useStyles();
    const narration = useInstance(InsightsNarration);
    const name = clientDisplayName(cell.clientName);
    const tipName = narration.shortName(cell.clientName);

    // A finger has nothing to rest, and a tap opens the detail that names them anyway.
    const isMouse = (event: PointerEvent<HTMLButtonElement>) => event.pointerType === 'mouse';
    const beside = (event: PointerEvent<HTMLButtonElement>): ITipAnchor => ({ name: tipName, x: event.clientX + TIP_OFFSET.x, y: event.clientY + TIP_OFFSET.y });
    const pointerEntered = (event: PointerEvent<HTMLButtonElement>) => {
        if (isMouse(event)) {
            onTipEnter(beside(event));
        }
    };
    const pointerMoved = (event: PointerEvent<HTMLButtonElement>) => {
        if (isMouse(event)) {
            onTipMove(beside(event));
        }
    };
    // Keyboard focus only: a click focuses the button too, and so does the detail dialog handing
    // focus back when it closes, which would leave the tip under the cell with no pointer to end it.
    const focused = (event: FocusEvent<HTMLButtonElement>) => {
        if (!isKeyboardFocus(event.currentTarget)) {
            return;
        }
        const rect = event.currentTarget.getBoundingClientRect();
        onTipEnter({ name: tipName, x: rect.left, y: rect.bottom + FOCUS_GAP });
    };
    const width = cellWidthOf(radius);
    const height = radius * 2;
    const faceSize = height - RIM * 2;

    return (
        <button
            type="button"
            className={classes.cell}
            style={{ left: cx - width / 2, top: cy - radius, width, height, background: READING_COLOR[cell.reading], opacity: isDimmed ? 0.22 : 1 }}
            data-id="honeycomb-cell"
            data-reading={cell.reading}
            data-dimmed={String(isDimmed)}
            onClick={onSelect}
            onPointerEnter={pointerEntered}
            onPointerMove={pointerMoved}
            onPointerLeave={onTipLeave}
            onFocus={focused}
            onBlur={onTipLeave}
        >
            <span className={classes.face}>
                <PersonAvatar size={faceSize} identity={cell.identity} name={name} />
            </span>
            <span className={`${classes.hidden} mp-mask`}>{name}: {READING_LABEL[cell.reading]}</span>
        </button>
    );
};

/** The client's name, drawn by the map at the point the tip holds. */
const HoneycombTip: FC<{ tip: ICursorTip }> = ({ tip }) => {
    const classes = useStyles();
    return (
        <span className={classes.tip} style={{ left: tip.x, top: tip.y }} data-id="honeycomb-tip" aria-hidden="true">
            <span className="mp-mask">{tip.name}</span>
        </span>
    );
};

/**
 * The caseload as a honeycomb, one cell per client: their photo, rimmed in the colour of their
 * reading: something that happened, or where they sit against their own baseline.
 *
 * A list answers "what is outstanding"; this answers "where should I look", which is the question a
 * reader opens the page with. It holds a whole book of clients in a single glance where twenty-five
 * rows would not, and it borrows the care map's own hexagon so the agency view and the client view
 * read as the same product.
 *
 * Four readings and no more: an incident, worth watching, steady, or not enough on record to say.
 * They are the digest's own readings, which every other surface draws the client with too. The
 * colour key, which counts and narrows by them, sits with the report above the map.
 */
export const HoneycombCaseloadMap: FC<{
    cells: IHoneycombCell[];
    /** When the page is narrowed, the clients still in scope. Everyone else stays but recedes. */
    matchingIds?: string[];
    /** The largest a cell is drawn; smaller where the page has a set amount of room, as on paper. */
    cellRadius?: number;
    /** Opens the client's detail view. Named for what it does, not for where it happens to lead. */
    onSelectClient: (careMapId: string) => void;
}> = ({ cells, matchingIds, cellRadius = RADIUS, onSelectClient }) => {
    const classes = useStyles();
    const { ref: wrapRef, width: wrapWidth } = useWidth();
    const mapRef = useRef<HTMLDivElement>(null);
    const cursorTip = useCursorTip(mapRef);

    if (cells.length === 0) {
        return null;
    }

    // Dropping the non-matching cells would renumber the grid and move every remaining client, which
    // costs the reader the one thing the honeycomb is for: knowing where a given client sits.
    const matching = matchingIds ? new Set(matchingIds) : undefined;

    const { columns, radius, width, height } = layoutOf({ count: cells.length, wrapWidth, maxRadius: cellRadius });

    return (
        <div className={classes.map} data-id="honeycomb-caseload-map" ref={mapRef}>
            <div className={classes.wrap} ref={wrapRef}>
                <div
                    className={classes.field}
                    style={{ width: Math.ceil(width), height: Math.ceil(height) }}
                    role="group"
                    aria-label={`Caseload of ${cells.length} clients, each shown against their own baseline`}
                >
                    {cells.map((cell, index) => {
                        const { cx, cy } = hexCentre({ column: index % columns, row: Math.floor(index / columns), radius });

                        return (
                            <HoneycombCell
                                key={cell.careMapId}
                                cell={cell}
                                cx={cx}
                                cy={cy}
                                // Each edge sits √3/2 of the radius from the centre, so neighbours end up √3 times further apart than the radius each gives up.
                                radius={radius - GAP / Math.sqrt(3)}
                                isDimmed={matching ? !matching.has(cell.careMapId) : false}
                                onSelect={() => onSelectClient(cell.careMapId)}
                                onTipEnter={cursorTip.enter}
                                onTipMove={cursorTip.move}
                                onTipLeave={cursorTip.leave}
                            />
                        );
                    })}
                </div>
            </div>

            {cursorTip.tip && <HoneycombTip tip={cursorTip.tip} />}
        </div>
    );
};
